import React from "react";
import { ColumnsType } from "antd/lib/table";
import { trailerTypeList } from "components/form/types";
import dayjs from "dayjs";
import { truncateText } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";

interface IColumnsTableReport {
  inboundDocumentsTypes: string[];
  outboundDocumentsTypes: string[];
}

const ColumnsTableReport = ({ inboundDocumentsTypes, outboundDocumentsTypes }: IColumnsTableReport) => {
  const dateTimeFormat = "DD/MM/YYYY HH:mm";
  const BOX_TRAILER = useTranslation({ id: "box_trailer" });
  const BULK_TRAILER = useTranslation({ id: "bulk_trailer" });
  const CURTAINSIDER = useTranslation({ id: "curtainsider" });
  const LIGHTWEIGHT_CURTAINSIDER = useTranslation({ id: "lightweight_curtainsider" });
  const FLATBED = useTranslation({ id: "flatbed" });
  const TANKER = useTranslation({ id: "tanker" });
  const OTHERS = useTranslation({ id: "fleet_channel_others" });
  const INBOUND = useTranslation({ id: "inbound" });
  const OUTBOUND = useTranslation({ id: "outbound" });
  const STRUCTURE = useTranslation({ id: "structure" });
  const UNIT = useTranslation({ id: "unit" });
  const LOCATION_CODE = useTranslation({ id: "location_code" });
  const PLATE = useTranslation({ id: "plate" });
  const INBOUND_TRAILERS_PLATES = useTranslation({ id: "inbound_trailers_plates" });
  const DRIVER = useTranslation({ id: "driver" });
  const CPF_INITIALS = useTranslation({ id: "identification_initials" });
  const CARRIER = useTranslation({ id: "carrier" });
  const INBOUND_PROCESS = useTranslation({ id: "inbound_process" });
  const INBOUND_OBSERVATION = useTranslation({ id: "inbound_observation" });
  const CHECKIN_DATE = useTranslation({ id: "checkin_date" });
  const INBOUND_EVENT_TIME = useTranslation({ id: "inbound_event_time" });
  const OUTBOUND_EVENT_TIME = useTranslation({ id: "outbound_event_time" });
  const OUTBOUND_PROCESS = useTranslation({ id: "outbound_process" });
  const OUTBOUND_TRAILERS_PLATES = useTranslation({ id: "outbound_trailers_plates" });
  const OUTBOUND_OBSERVATION = useTranslation({ id: "outbound_observation" });
  const VEHICLE_PLATE_SITUATION = useTranslation({ id: "vehicle_plate_situation" });
  const VEHICLE_REASON = useTranslation({ id: "vehicle_reason" });
  const VEHICLE_NAME = useTranslation({ id: "vehicle_name" });
  const DRIVER_STATUS = useTranslation({ id: "driver_status" });
  const DRIVER_REASON = useTranslation({ id: "driver_reason" });
  const VERIFICATION_FROTA_LEGAL = useTranslation({ id: "verification_frota_legal" });
  const NOT_VERIFIED = useTranslation({ id: "not_verified" });
  const VERIFIED = useTranslation({ id: "verified" });
  const TRAILER_TYPE = useTranslation({ id: "trailer_type" });
  const FLEET_CHANNEL = useTranslation({ id: "fleet_channel" });

  const trailerTypeTranslationByKey: Record<string, string> = {
    box_trailer: BOX_TRAILER,
    curtainsider: CURTAINSIDER,
    flatbed: FLATBED,
    lightweight_curtainsider: LIGHTWEIGHT_CURTAINSIDER,
    bulk_trailer: BULK_TRAILER,
    tanker: TANKER,
    others: OTHERS,
  };

  const trailerTypeDescriptionById = trailerTypeList.reduce((descriptions, trailerType) => {
    return {
      ...descriptions,
      [trailerType.id]: trailerTypeTranslationByKey[trailerType.description],
    };
  }, {} as Record<number, string>);

  const renderDescriptionById = (value: number | null | undefined, descriptions: Record<number, string>) => {
    if (value == null) return "-";

    const id = Number(value);
    return id >= 0 && descriptions[id] ? descriptions[id] : "-";
  };

  const document = (documentsTypes, label) =>
    documentsTypes?.map((documentItem) => {
      return {
        title: `${documentItem} ${label}`,
        dataIndex: `${documentItem} ${label}`,
        width: 150,
        key: `${documentItem} ${label}`,
        render: (item: ColumnsType<string>) => `${item === undefined || item === null ? "-" : item}`,
      };
    });

  return [
    {
      title: STRUCTURE,
      dataIndex: "structure",
      width: 100,
      key: "structure",
      render: (structure: ColumnsType<string>) => `${structure}`,
      fixed: "left",
    },
    {
      title: UNIT,
      dataIndex: "location",
      width: 150,
      key: "location",
      render: (location: ColumnsType<string>) => `${location}`,
      fixed: "left",
    },
    {
      title: LOCATION_CODE,
      dataIndex: "locationCode",
      width: 150,
      key: "locationCode",
      render: (locationCode: ColumnsType<string>) => `${locationCode === undefined || locationCode === null ? "-" : locationCode}`,
      fixed: "left",
    },
    {
      title: PLATE,
      dataIndex: "vehiclePlate",
      width: 120,
      key: "vehiclePlate",
      render: (vehiclePlate: ColumnsType<string>) => `${vehiclePlate}`,
      fixed: "left",
    },
    {
      title: VEHICLE_PLATE_SITUATION,
      dataIndex: "vehicleStatus",
      width: 150,
      key: "vehicleStatus",
      render: (vehicleStatus: ColumnsType<string>) => `${vehicleStatus || "-"}`,
    },
    {
      title: VEHICLE_NAME,
      dataIndex: "vehicleName",
      width: 200,
      key: "vehicleName",
      render: (vehicleName: ColumnsType<string>) => `${vehicleName || "-"}`,
    },
    {
      title: VEHICLE_REASON,
      dataIndex: "vehicleReason",
      width: 230,
      key: "vehicleReason",
      render: (vehicleReason: ColumnsType<string>) => `${vehicleReason || "-"}`,
    },
    {
      title: INBOUND_TRAILERS_PLATES,
      dataIndex: "inboundTrailersPlates",
      width: 200,
      key: "inboundTrailersPlates",
      render: (inboundTrailersPlates: string) =>
        `${inboundTrailersPlates === undefined || inboundTrailersPlates === "" || inboundTrailersPlates === null ? "-" : inboundTrailersPlates}`,
    },
    {
      title: DRIVER,
      dataIndex: "driver",
      width: 230,
      key: "driver",
      render: (driver: ColumnsType<string>) => (driver === undefined || driver?.length === 0 ? "-" : <span>{driver.join("\n")}</span>),
    },
    {
      title: CPF_INITIALS,
      dataIndex: "cpf",
      width: 180,
      key: "cpf",
      render: (cpf: ColumnsType<string>) => (cpf === undefined || cpf?.length === 0 ? "-" : <span>{cpf.join("\n")}</span>),
    },
    {
      title: DRIVER_STATUS,
      dataIndex: "driverStatus",
      width: 170,
      key: "driverStatus",
      render: (driverStatus: ColumnsType<string>) => `${driverStatus || "-"}`,
    },
    {
      title: DRIVER_REASON,
      dataIndex: "driverReason",
      width: 230,
      key: "driverReason",
      render: (driverReason: ColumnsType<string>) => `${driverReason || "-"}`,
    },
    {
      title: VERIFICATION_FROTA_LEGAL,
      dataIndex: "verified",
      width: 200,
      key: "verified",
      render: (verified: ColumnsType<string>) => `${verified ? VERIFIED : NOT_VERIFIED}`,
    },
    {
      title: TRAILER_TYPE,
      dataIndex: "trailerType",
      width: 180,
      key: "trailerType",
      render: (trailerType: number) => renderDescriptionById(trailerType, trailerTypeDescriptionById),
    },
    {
      title: FLEET_CHANNEL,
      dataIndex: "saleChannelDescription",
      width: 170,
      key: "saleChannelDescription",
      render: (saleChannelDescription: string) => `${saleChannelDescription ?? "-"}`,
    },
    {
      title: CARRIER,
      dataIndex: "carrier",
      width: 350,
      key: "carrier",
      render: (carrier: string) => truncateText(carrier),
    },
    {
      title: INBOUND_PROCESS,
      dataIndex: "inboundProcessDescription",
      width: 150,
      key: "inboundProcessDescription",
      render: (inboundProcessDescription: ColumnsType<string>) => `${inboundProcessDescription}`,
    },
    ...(document(inboundDocumentsTypes, INBOUND) || []),
    {
      title: INBOUND_OBSERVATION,
      dataIndex: "inboundObservation",
      width: 200,
      key: "inboundObservation",
      render: (inboundObservation: ColumnsType<string>) => `${inboundObservation === undefined || inboundObservation === null ? "-" : inboundObservation}`,
    },
    {
      title: CHECKIN_DATE,
      dataIndex: "checkinEventTime",
      width: 150,
      key: "checkinEventTime",
      render: (checkinEventTime: string) => (checkinEventTime === "" || checkinEventTime === null ? "-" : `${dayjs.utc(checkinEventTime).local().format(dateTimeFormat)}h`),
    },
    {
      title: INBOUND_EVENT_TIME,
      dataIndex: "inboundEventTime",
      width: 150,
      key: "inboundEventTime",
      render: (inboundEventTime: string) => (inboundEventTime === "" || inboundEventTime === null ? "-" : `${dayjs.utc(inboundEventTime).local().format(dateTimeFormat)}h`),
    },
    {
      title: OUTBOUND_EVENT_TIME,
      dataIndex: "outboundEventTime",
      width: 110,
      key: "outboundEventTime",
      render: (outboundEventTime: string) =>
        outboundEventTime === "" || outboundEventTime === undefined || outboundEventTime === null ? "-" : `${dayjs.utc(outboundEventTime).local().format(dateTimeFormat)}h`,
    },
    {
      title: OUTBOUND_PROCESS,
      dataIndex: "outboundProcessDescription",
      width: 150,
      key: "outboundProcessDescription",
      render: (outboundProcessDescription: ColumnsType<string>) =>
        `${outboundProcessDescription === undefined || outboundProcessDescription === null ? "-" : outboundProcessDescription}`,
    },
    ...(document(outboundDocumentsTypes, OUTBOUND) || []),
    {
      title: OUTBOUND_TRAILERS_PLATES,
      dataIndex: "outboundTrailersPlates",
      width: 200,
      key: "outboundTrailersPlates",
      render: (outboundTrailersPlates: ColumnsType<string>) => `${outboundTrailersPlates === undefined || outboundTrailersPlates === null ? "-" : outboundTrailersPlates}`,
    },
    {
      title: OUTBOUND_OBSERVATION,
      dataIndex: "outboundObservation",
      width: 200,
      key: "outboundObservation",
      render: (outboundObservation: string) => `${outboundObservation === "" || outboundObservation === undefined || outboundObservation === null ? "-" : outboundObservation}`,
    },
  ];
};

export default ColumnsTableReport;
